import { createGlobalStyle } from 'styled-components';
import {
    CURSOR_DEFAULT,
    CURSOR_INTERACTIVE,
    CURSOR_TEXT,
    CURSOR_DISABLED
} from './cursors';
import {media} from "./breakpoints.js"; // Adjust path if your cursors file is elsewhere

const GlobalStyle = createGlobalStyle`
    :root {
        // Keep your color variables
        --primary-color: #ff3399;
        --secondary-color: #00ffcc;
        --tertiary-color: #9933ff;
        --background-color: #000000;
        --grid-color: rgba(255, 51, 153, 0.25);
        --text-color: #ffffff;
        --text-muted: #bbbbbb; // WCAG AAA compliant on dark backgrounds (7.5:1 contrast)
        --card-bg: #1d1d1d;
        --tag-bg: #2a2a2a;
        --link-hover-color: #535bf2; // Example, adjust if needed
    }

    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        font-family: 'JetBrains Mono', Consolas, Monaco, 'Lucida Console', monospace;
    }

    html {
        /* Improve smooth scrolling experience */
        scroll-behavior: smooth;
        /* Prevent text size inflation on some mobile devices */
        -webkit-text-size-adjust: 100%;
        -moz-text-size-adjust: 100%;
        text-size-adjust: 100%;
    }

    body {
        background-color: var(--background-color);
        color: var(--text-color);
        line-height: 1.6;
        /* overflow-x: hidden; <-- REMOVED: Hiding overflow often masks the real layout problems. */
        /* Let's fix the causes first. Add back ONLY if absolutely necessary after fixing elements. */
        cursor: ${CURSOR_DEFAULT};
        min-height: 100vh; // Ensure body takes at least full viewport height
        /* Improve text rendering */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    /* --- RESPONSIVENESS FIX --- */
    /* Make images, videos, svgs, and canvas elements scale correctly */
    img,
    video,
    svg,
    canvas {
        max-width: 100%; /* Prevent them from exceeding their container's width */
        height: auto;    /* Maintain aspect ratio automatically */
        display: block;  /* Removes potential extra bottom space for inline elements */
        /* Consider adding vertical-align: middle; if display: block causes issues with text alignment */
    }

    /* Custom cursors for different states (keep these) */
    a, button, [role="button"], input[type="submit"], input[type="button"],
    input[type="reset"], input[type="checkbox"], input[type="radio"],
    label[for], select, summary, .clickable {
        cursor: ${CURSOR_INTERACTIVE};
    }

    input[type="text"], input[type="email"], input[type="password"],
    input[type="number"], input[type="search"], input[type="tel"],
    input[type="url"], textarea {
        cursor: ${CURSOR_TEXT};
        caret-shape: block;
        caret-color: var(--primary-color) !important;
    }

    button:disabled, input:disabled, textarea:disabled, select:disabled,
    .disabled {
        cursor: ${CURSOR_DISABLED};
    }

    /* Base Link Styles */
    a {
        color: var(--text-color);
        text-decoration: none;
        /* Optional: Add a subtle transition */
        /* transition: color 0.2s ease; */
    }

    /* Input focus styles (keep these) */
    input:focus, textarea:focus {
        outline: none !important;
        /* Optional: Add a visual focus indicator if removing outline */
        /* box-shadow: 0 0 0 2px rgba(0, 255, 204, 0.5); */
    }

    /* Aesthetic Word Styling (keep this) */
    .aesthetic-word {
        white-space: nowrap;
        display: inline-block; /* Be mindful this can cause overflow if not enough space */
    }

    /* Ensure root container fills height for flex layouts in App.jsx */
    #root {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
    }

    html, body, #root {
        width: 100%;
        min-height: 100vh;
    }

    h1 { font-size: clamp(2rem, 5vw, 3rem); }
    h2 { font-size: clamp(1.5rem, 4vw, 2.5rem); }
    h3 { font-size: clamp(1.2rem, 3vw, 1.8rem); }
    p, li { font-size: clamp(0.9rem, 2vw, 1rem); }

    /* Add consistent breakpoints */
    @media (max-width: 320px) {
        /* Ultra small phones */
        html { font-size: 12px; } /* Slightly smaller base font */
    }

    /* Responsive media utilities */
    .responsive-img {
        max-width: 100%;
        height: auto;
    }

    .mobile-stack {
        ${media.md} {
            display: flex;
            flex-direction: column;
        }
    }

    .mobile-hide {
        ${media.sm} {
            display: none;
        }
    }

    .desktop-hide {
        display: none;
        ${media.md} {
            display: block;
        }
    }

    .responsive-margin {
        margin: 30px 0;

        ${media.md} {
            margin: 20px 0;
        }

        ${media.sm} {
            margin: 15px 0;
        }
    }

    .responsive-padding {
        padding: 30px;

        ${media.md} {
            padding: 20px;
        }

        ${media.sm} {
            padding: 15px;
        }

        ${media.xs} {
            padding: 10px;
        }
    }

    /* ========================================
       ACCESSIBILITY: Prefers Reduced Motion
       ======================================== */
    /* Respect users with motion sensitivity or vestibular disorders */
    @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
            /* Reduce all animations to instant (0.01ms is minimum, 0ms can break some animations) */
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;

            /* Reduce all transitions to instant */
            transition-duration: 0.01ms !important;
            transition-delay: 0s !important;
        }

        /* Disable smooth scrolling */
        html {
            scroll-behavior: auto !important;
        }

        /* Specifically target common animation patterns */
        /* This ensures no animations play at all for sensitive users */
        * {
            animation-play-state: paused !important;
        }
    }
`;



export default GlobalStyle;